// src/api/service.js
import { clearAuthTokens, getAccessToken, refreshAccessToken } from 'api/authTokens';

const API_BASE_URL = import.meta.env.VITE_APP_API_BASE_URL || '';

async function request(path, options = {}) {
  const url = `${API_BASE_URL}${path}`;
  const { _retry, ...fetchOptions } = options;

  const headers = {
    ...(fetchOptions.headers || {})
  };

  if (!(fetchOptions.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  const token = getAccessToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(url, {
    headers,
    credentials: 'include', // if you ever use cookies/session
    ...fetchOptions
  });

  if (res.status === 401 && !_retry && !path.includes('/api/auth/token/')) {
    const nextToken = await refreshAccessToken();
    if (nextToken) {
      return request(path, { ...options, _retry: true });
    }
  }

  if (res.status === 401) {
    clearAuthTokens();
    window.location.href = '/auth/login';
    return;
  }

  const contentType = res.headers.get('content-type') || '';

  if (!res.ok) {
    // Try to read JSON error if present
    if (contentType.includes('application/json')) {
      const errData = await res.json();
      console.error('API error', url, res.status, errData);
      throw new Error(
        errData.detail || errData.message || `Request failed: ${res.status}`
      );
    } else {
      const text = await res.text();
      console.error('API error', url, res.status, text);
      throw new Error(`Request failed: ${res.status} ${text}`);
    }
  }

  if (res.status === 204) return null;

  if (contentType.includes('application/json')) {
    return res.json();
  }

  const text = await res.text();
  throw new Error(`Expected JSON but got: ${text.slice(0, 200)}...`);
}

export function apiGet(path) {
  return request(path, { method: 'GET' });
}

/**
 * GET every page of a paginated list endpoint and return the combined rows.
 *
 * The API paginates at 50 by default, so a plain apiGet() silently returns
 * only the first page — which is why lists appeared truncated. This follows
 * the `next` links until exhausted.
 *
 * `maxPages` is a safety stop so a runaway/looping `next` can't hang the tab.
 */
export async function apiGetAll(path, { pageSize = 200, maxPages = 100 } = {}) {
  const sep = path.includes('?') ? '&' : '?';
  let next = `${path}${sep}page_size=${pageSize}`;
  const rows = [];

  for (let page = 0; page < maxPages && next; page += 1) {
    // eslint-disable-next-line no-await-in-loop
    const data = await request(next, { method: 'GET' });
    if (Array.isArray(data)) return data; // endpoint isn't paginated
    rows.push(...(data?.results || []));
    if (!data?.next) break;
    // `next` is an absolute URL; strip the origin so it goes through API_BASE_URL.
    try {
      const url = new URL(data.next);
      next = `${url.pathname}${url.search}`;
    } catch {
      next = data.next;
    }
  }
  return rows;
}

export function apiPost(path, body) {
  return request(path, {
    method: 'POST',
    // FormData must be passed through untouched — stringifying it yields "{}"
    // and the upload silently arrives empty.
    body: body instanceof FormData ? body : JSON.stringify(body)
  });
}

export function apiPostForm(path, formData) {
  return request(path, {
    method: 'POST',
    body: formData
  });
}

export function apiPatch(path, body) {
  return request(path, {
    method: 'PATCH',
    body: body instanceof FormData ? body : JSON.stringify(body)
  });
}
export function apiDelete(path) {
  return request(path, {
    method: 'DELETE'
  });
}
